import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import DeptSwitcher, { ccDepartments } from '../components/ContactCenter/DeptSwitcher';
import ExportButton from '../components/ContactCenter/ExportButton';
import { useAuth } from '../context/AuthContext';
import { fmtCount, fmtClock, fmtPct } from '../components/ContactCenter/format';
import { btn, c, card, tbl } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Outbound — a department's agent-dialled calls and what
// came of them. Every rate here is derived from the agent's wrap-up code,
// because on a leg the agent placed Genesys's "answered" event does not fire;
// the page says so rather than implying the far end picked up.
//
// Undispositioned is shown as its own figure, in amber past a fifth of dials:
// it is the number that decides how much the other rates can be trusted, and
// the old page turned it amber at the same point.

const pacificToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
const firstOfMonth = (day) => `${day.slice(0, 8)}01`;
function lastMonth(today) {
  const [y, m] = today.split('-').map(Number);
  return {
    start: new Date(Date.UTC(y, m - 2, 1)).toISOString().slice(0, 10),
    end: new Date(Date.UTC(y, m - 1, 0)).toISOString().slice(0, 10),
  };
}
// The first day the portal holds Contact Center data; the server clamps to it.
const DATA_FLOOR = '2026-06-04';
const UNDISPOSITIONED_AMBER = 20;
const MODES = [['today', 'Today'], ['mtd', 'Month to date'], ['last-month', 'Last month'], ['custom', 'Custom']];
const EMPTY = { today: 'today', mtd: 'this month', 'last-month': 'last month', custom: 'in this period' };

function Stat({ label, value, sub, tone }) {
  return (
    <section style={s.stat} aria-label={label}>
      <div style={s.sLabel}>{label}</div>
      <div style={{ ...s.sValue, color: tone === 'warn' ? c.warningText : c.textStrong }}>{value}</div>
      {sub && <div style={s.sSub}>{sub}</div>}
    </section>
  );
}

export default function ContactCenterOutbound() {
  const today = pacificToday();
  const { user } = useAuth();
  const [dept, setDept] = useState(() => ccDepartments(user, 'outbound')[0]?.id || 'collections');
  const [mode, setMode] = useState('mtd');
  const [custom, setCustom] = useState({ start: '', end: '' });
  // A custom range is asked for only once both ends are chosen, in order:
  // half a range would otherwise fall back to the server's month to date and
  // show figures for a period nobody picked.
  const customReady = custom.start && custom.end && custom.start <= custom.end;
  const range = mode === 'today' ? { start: today, end: today }
    : mode === 'last-month' ? lastMonth(today)
      : mode === 'custom' ? (customReady ? custom : null)
        : { start: firstOfMonth(today), end: today };

  const q = useQuery({
    queryKey: ['cc-outbound', dept, range?.start, range?.end],
    queryFn: () => api.get('/contact-center/outbound', { params: { ...range, dept } }),
    enabled: !!range,
    // A closed period does not change; only a window reaching today refreshes.
    refetchInterval: range?.end === today ? 60 * 1000 : false,
  });
  const d = q.data;
  const undispositionedHigh = (d?.rates.undispositionedPct ?? 0) > UNDISPOSITIONED_AMBER;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Contact Center"
        title={d?.deptLabel ? `Outbound · ${d.deptLabel}` : 'Outbound'}
        subtitle={d ? `${fmtCount(d.dials)} dials · ${d.range.label}` : 'Agent-dialled calls'}
        actions={(
          <div style={s.row}>
            <DeptSwitcher view="outbound" value={dept} onChange={setDept} />
            <span style={s.row} role="group" aria-label="Period">
              {MODES.map(([key, label]) => (
                <button key={key} type="button" onClick={() => setMode(key)} aria-pressed={mode === key}
                  style={mode === key ? s.pillOn : s.pill}>{label}</button>
              ))}
              {mode === 'custom' && (
                <>
                  <input id="cc-outbound-start" type="date" aria-label="Start date" min={DATA_FLOOR} max={today}
                    value={custom.start} onChange={(e) => setCustom((v) => ({ ...v, start: e.target.value }))} style={s.input} />
                  <input id="cc-outbound-end" type="date" aria-label="End date" min={DATA_FLOOR} max={today}
                    value={custom.end} onChange={(e) => setCustom((v) => ({ ...v, end: e.target.value }))} style={s.input} />
                </>
              )}
            </span>
            {range && (
              <ExportButton path="/contact-center/outbound/export" params={{ dept, start: range.start, end: range.end }}
                filename={`Contact-Center-Outbound-${dept}_${range.start}_${range.end}.xlsx`} />
            )}
          </div>
        )}
        alignActions="end"
      />

      {mode === 'custom' && !range && (
        <p style={s.empty}>{custom.start && custom.end ? 'The start date is after the end date.' : 'Choose a start and an end date.'}</p>
      )}
      {range && q.isLoading && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load Outbound: {q.error.message}</div>}

      {d && d.dials === 0 && d.campaigns.totals.campaigns === 0 && (
        <p style={s.empty}>No outbound dials {EMPTY[mode]}{mode === 'today' || mode === 'mtd' ? ' yet' : ''}.</p>
      )}

      {d && d.dials > 0 && (
        <>
          <div style={s.stats}>
            <Stat label="Outbound calls" value={fmtCount(d.dials)} sub="legs the agent placed" />
            <Stat label="Right-party contact" value={fmtCount(d.dispositions.right_party)}
              sub={`${fmtPct(d.rates.rightPartyPct)} of dials · ${fmtCount(d.dispositions.reachedLive)} reached a live line (${fmtPct(d.rates.reachedLivePct)})`} />
            <Stat label="Not connected" value={fmtCount(d.dispositions.no_contact)} sub="no answer, invalid, disconnected" />
            <Stat label="Undispositioned" value={fmtCount(d.dispositions.undispositioned)}
              sub={`${fmtPct(d.rates.undispositionedPct)} of dials`} tone={undispositionedHigh ? 'warn' : null} />
            <Stat label="Outbound AHT" value={fmtClock(d.aht)} sub="per dial" />
          </div>

          <p style={s.note}>
            Contact rates are a floor: they count only what an agent recorded. A dial with no wrap-up code cannot be
            told apart from one that reached nobody, so it is counted as undispositioned rather than as a non-contact.
            {d.dispositions.unclassifiedDetail.length > 0 && (
              <> Not yet classified: {d.dispositions.unclassifiedDetail.map((u) => `${u.name} (${u.dials})`).join(', ')}.</>
            )}
          </p>

          <section style={s.card} aria-label="Disposition mix">
            <h2 style={s.h2}>Disposition mix · outbound wrap-ups</h2>
            {d.mix.map((m) => (
              <div key={m.name} style={s.barRow}>
                <span style={s.barName}>{m.name}</span>
                <span style={s.barTrack}><span style={{ ...s.barFill, width: `${Math.max(1, m.pct || 0)}%` }} /></span>
                <span style={s.barNum}>{fmtCount(m.dials)} · {fmtPct(m.pct)}</span>
              </div>
            ))}
          </section>

          <section style={s.card} aria-label="Agents">
            <h2 style={s.h2}>Agents</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Agent</th><th style={s.th}>Team</th><th style={s.thNum}>Dials</th>
                    <th style={s.thNum}>Right-party</th><th style={s.thNum}>Undispositioned</th>
                    <th style={s.thNum}>Avg handle</th><th style={s.th}>Top dispositions</th>
                  </tr>
                </thead>
                <tbody>
                  {d.agents.map((a) => (
                    <tr key={a.userId} style={s.tr}>
                      <td style={s.td}>{a.name || 'Unknown agent'}</td>
                      <td style={s.td}>{a.pod}</td>
                      <td style={s.num}>{fmtCount(a.dials)}</td>
                      <td style={s.num}>{fmtCount(a.rightParty)}{a.rightPartyPct != null && <span style={s.sub}> · {fmtPct(a.rightPartyPct)}</span>}</td>
                      <td style={s.num}>{fmtCount(a.undispositioned)}</td>
                      <td style={s.num}>{fmtClock(a.aht)}</td>
                      <td style={s.td}>{a.top.map((t) => `${t.name} (${t.dials})`).join(' · ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
      {d && d.campaigns.totals.campaigns > 0 && <Campaigns c={d.campaigns} />}
    </div>
  );
}

// OBC's campaigns are agentless: nobody sets a wrap-up code, so the outcome
// of a dial is Genesys's own call analysis. Shares are of CLASSIFIED sessions,
// never of attempts, because an attempt Genesys did not classify would
// otherwise deflate every rate at once — the page says which it is using.
function Campaigns({ c }) {
  const t = c.totals;
  const o = c.outcomes;
  return (
    <>
      <div style={s.stats}>
        <Stat label="Campaigns" value={fmtCount(t.campaigns)} sub={`${fmtCount(t.running)} running`} />
        <Stat label="List size" value={fmtCount(t.listSize)} sub="contacts loaded" />
        <Stat label="Attempted" value={fmtCount(t.attempted)} sub={`${fmtCount(t.recorded)} recorded by Genesys`}
          tone={t.residualAlarm ? 'warn' : null} />
        <Stat label="Human answered" value={fmtCount(o.human)}
          sub={o.classified > 0 ? `${fmtPct(o.humanPct)} of ${fmtCount(o.classified)} classified` : 'nothing classified yet'} />
        <Stat label="Answering machine" value={fmtCount(o.machine)} sub={fmtPct(pctOf(o.machine, o.classified))} />
      </div>

      {t.residualAlarm && (
        <p style={s.alarm} role="alert">
          Genesys holds records for only {fmtCount(t.recorded)} of {fmtCount(t.attempted)} attempts
          ({fmtPct(t.unrecordedPct)} missing). The outcome shares below describe the dials it did record.
        </p>
      )}

      <section style={s.card} aria-label="Campaign list">
        <h2 style={s.h2}>Campaigns</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Campaign</th><th style={s.th}>List</th><th style={s.thNum}>List size</th>
                <th style={s.thNum}>Attempted</th><th style={s.thNum}>Human</th><th style={s.thNum}>Machine</th>
                <th style={s.thNum}>No answer</th><th style={s.thNum}>Bad number</th><th style={s.thNum}>Remaining</th>
                <th style={s.th}>State</th>
              </tr>
            </thead>
            <tbody>
              {c.campaigns.map((r) => (
                <tr key={r.campaignId} style={s.tr}>
                  <td style={s.td}>
                    {r.name}
                    {r.residualAlarm && <span style={s.flag} title={`${fmtPct(r.unrecordedPct)} of attempts have no record in Genesys`}> ⚑</span>}
                  </td>
                  <td style={s.td}>{r.contactList || '—'}<span style={s.sub}>{r.dialingMode ? ` · ${r.dialingMode}` : ''}</span></td>
                  <td style={s.num}>{r.listSize == null ? '—' : fmtCount(r.listSize)}</td>
                  <td style={s.num}>{fmtCount(r.attempted)}</td>
                  <td style={s.num}>{fmtCount(r.outcomes.human)}{r.outcomes.humanPct != null && <span style={s.sub}> · {fmtPct(r.outcomes.humanPct)}</span>}</td>
                  <td style={s.num}>{fmtCount(r.outcomes.machine)}</td>
                  <td style={s.num}>{fmtCount(r.outcomes.noAnswer)}</td>
                  <td style={s.num}>{fmtCount(r.outcomes.badNumber)}</td>
                  <td style={s.num}>{r.remaining == null ? '—' : fmtCount(r.remaining)}</td>
                  <td style={s.td}>
                    {r.running ? <span style={s.on}>Running</span>
                      : r.lastRan ? <span style={s.sub}>Idle · last ran {r.lastRan}</span>
                        : <span style={s.sub}>No dials in this period</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={s.foot}>
          An agentless dial has no wrap-up code, so these outcomes are Genesys&rsquo;s own call analysis. Shares are of
          classified sessions, not of attempts.
          {o.otherDetail.length > 0 && ` Other verdicts: ${o.otherDetail.map((x) => `${x.verdict} (${x.sessions})`).join(', ')}.`}
        </p>
      </section>
    </>
  );
}

const pctOf = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : null);

const s = {
  page:    { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  row:     { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  pill:    { ...btn.secondaryXs },
  pillOn:  { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  input:   { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface },
  error:   { fontSize: '13px', color: c.dangerText },
  empty:   { fontSize: '13px', color: c.textMuted },
  stats:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' },
  stat:    { ...card.base, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '2px' },
  sLabel:  { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted },
  sValue:  { fontSize: '26px', fontWeight: 650, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
  sSub:    { fontSize: '11px', color: c.textMuted, lineHeight: 1.4 },
  note:    { fontSize: '11.5px', color: c.textMuted, margin: 0, lineHeight: 1.5 },
  card:    { ...card.base, padding: '14px 18px' },
  h2:      { fontSize: '13px', fontWeight: 650, color: c.text, margin: '0 0 10px' },
  barRow:  { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', padding: '3px 0' },
  barName: { minWidth: '220px', color: c.textStrong },
  barTrack:{ flex: 1, height: '8px', background: c.surfaceSunk, borderRadius: '999px', overflow: 'hidden' },
  barFill: { display: 'block', height: '100%', background: c.navy },
  barNum:  { minWidth: '110px', textAlign: 'right', color: c.textMuted, fontVariantNumeric: 'tabular-nums' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:      { ...tbl.th, textAlign: 'left' },
  thNum:   { ...tbl.thR, whiteSpace: 'nowrap' },
  td:      { ...tbl.td, color: c.textStrong },
  num:     { ...tbl.tdR, color: c.textStrong },
  tr:      { ...tbl.tr },
  sub:     { fontSize: '11px', color: c.textMuted },
  alarm:   { fontSize: '12.5px', fontWeight: 600, color: c.dangerText, background: c.dangerTint, padding: '10px 14px', borderRadius: '8px', margin: 0 },
  flag:    { color: c.dangerText },
  on:      { fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px', background: c.successTint, color: c.successText },
  foot:    { fontSize: '11px', color: c.textMuted, margin: '8px 0 0', lineHeight: 1.45 },
};
